/**
 * Client source identification for the KozéCho backend.
 *
 * The Django API tags every request with an `X-Client-Source` header so we can
 * differentiate analytics, throttle limits, and roll out features per surface.
 * The values map to the apps that consume the API today/soon and MUST match
 * the backend allowlist `VALID_SOURCES` in
 * `kozecho-dispatch/apps/core/middleware/client_source.py`:
 *
 *   - `mobile-ios`        — the Expo / React Native app on iOS
 *   - `mobile-android`    — the Expo / React Native app on Android
 *   - `web-app`           — the authenticated web app (app.kozecho.com)
 *   - `web-marketing`     — the public vitrine site (kozecho.com)
 *   - `management`        — the management dashboard (admin.kozecho.com)
 *
 * Note: the backend uses an `"unknown"` sentinel internally for requests that
 * arrive without the header. Clients MUST NOT send `"unknown"` — it is not a
 * legitimate source value.
 */

export const CLIENT_SOURCES = {
  MOBILE_IOS: 'mobile-ios',
  MOBILE_ANDROID: 'mobile-android',
  WEB_APP: 'web-app',
  WEB_MARKETING: 'web-marketing',
  MANAGEMENT: 'management',
} as const;

export type ClientSource = (typeof CLIENT_SOURCES)[keyof typeof CLIENT_SOURCES];

/** HTTP header carrying the client identifier. Matches Django's `ClientSourceMiddleware`. */
export const CLIENT_SOURCE_HEADER = 'X-Client-Source';

/** HTTP header carrying the client semver (e.g. `1.4.2`, `2025.06.11`). */
export const CLIENT_VERSION_HEADER = 'X-Client-Version';

const CLIENT_SOURCE_VALUES = new Set<string>(Object.values(CLIENT_SOURCES));

/** Type guard: narrows a string to `ClientSource` when it's a known value. */
export function isValidClientSource(value: unknown): value is ClientSource {
  return typeof value === 'string' && CLIENT_SOURCE_VALUES.has(value);
}
